import { useResolveMetadataKey } from '@modules/ManageUnifiedFields/hooks/useResolveMetadataKey';
import { mapEQLReferenceToSelectedMetadata } from '@modules/ManageUnifiedFields/utils/mapEQLReferenceToSelectedMetadata';
import { Box, CircularProgress, Drawer, Stack } from '@mui/material';
import { MetadataDataType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { EditUnifiedFieldDrawerFooter } from '../EditUnifiedFieldDrawerFooter';
import { EditUnifiedFieldDrawerHeader } from '../EditUnifiedFieldDrawerHeader';
import { EditUnifiedFieldForm } from '../EditUnifiedFieldForm';

interface EditUnifiedFieldDrawerProps {
  open: boolean;
  onClose: () => void;
  metadataKey: string;
  onSubmit?: () => void;
}

export const EditUnifiedFieldDrawer = ({
  open,
  onClose,
  metadataKey,
  onSubmit,
}: EditUnifiedFieldDrawerProps) => {
  const { metadata, loading } = useResolveMetadataKey(metadataKey);
  const [metadataName, setMetadataName] = useState(metadata.displayText);
  const [metadataDescription, setMetadataDescription] = useState(
    metadata.description,
  );
  const [selectedMetadataValues, setReferencedKeys] = useState<string[]>([]);
  const [metadataType, setMetadataType] = useState(metadata.type);

  useEffect(() => {
    setMetadataName(metadata.displayText);
    setMetadataDescription(metadata.description);
    setMetadataType(metadata.type);
  }, [metadata.description, metadata.displayText, metadata.type]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.modal,
      }}
    >
      <Box
        sx={{
          width: 800,
          height: '100%',
        }}
      >
        {loading ? (
          <Stack justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Stack>
        ) : (
          <Stack direction="column" height="100%">
            <EditUnifiedFieldDrawerHeader name={metadata?.displayText ?? ''} />
            <EditUnifiedFieldForm
              name={metadata?.displayText ?? ''}
              setMetadataName={setMetadataName}
              description={metadata?.description ?? ''}
              setMetadataDescription={setMetadataDescription}
              type={metadataType ?? MetadataDataType.String}
              selectedMetadataValues={
                mapEQLReferenceToSelectedMetadata(
                  metadata?.references,
                  metadata.type,
                ) ?? []
              }
              setReferencedKeys={setReferencedKeys}
              setMetadataType={setMetadataType}
            />
            <EditUnifiedFieldDrawerFooter
              onClose={onClose}
              name={metadataName ?? metadata.displayText}
              description={metadataDescription ?? metadata.description}
              type={metadataType ?? metadata.type}
              referencedKeys={
                selectedMetadataValues.length > 0
                  ? selectedMetadataValues
                  : metadata.references?.map((ref) => ref.key)
              }
              metadataKey={metadataKey}
              onSubmit={onSubmit}
            />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};
